/**
 * AI Generation Service
 * Serviço para geração de legendas e prompts usando IA
 */

import { api } from '../lib/api';

// =============================================================================
// TIPOS
// =============================================================================

/**
 * @typedef {Object} CaptionItem
 * @property {string} text - Texto da legenda
 * @property {string} style - Estilo da legenda (engajamento, informativo, call-to-action)
 */

/**
 * @typedef {Object} PromptItem
 * @property {string} text - Texto do prompt
 * @property {string} focus - Foco do prompt (venda, engajamento, marca)
 */

/**
 * @typedef {Object} QuickAction
 * @property {string} id - ID da ação
 * @property {string} label - Label para exibição
 * @property {string} emoji - Emoji da ação
 * @property {string} description - Descrição da ação
 * @property {boolean} requires_product - Se requer nome do produto
 */

// =============================================================================
// SERVIÇO
// =============================================================================

export const aiGenerationService = {
  
  // ===========================================================================
  // GERAÇÃO DE LEGENDAS PARA POSTS
  // ===========================================================================
  
  /**
   * Gera legendas criativas para posts baseadas em uma imagem
   * @param {Object} params
   * @param {string} [params.imageBase64] - Imagem em base64
   * @param {string} [params.imageUrl] - URL da imagem (alternativa)
   * @param {string} [params.contentType='marketing'] - Tipo de conteúdo
   * @param {string} [params.userContext=''] - Contexto adicional
   * @param {string} [params.mimeType='image/jpeg'] - Tipo MIME
   * @returns {Promise<CaptionItem[]>} Lista de legendas geradas
   */
  async generatePostCaptions({
    imageBase64,
    imageUrl,
    contentType = 'marketing',
    userContext = '',
    mimeType = 'image/jpeg'
  }) {
    try {
      const response = await api.post('/api/ai/generate-post-captions', {
        image_base64: imageBase64,
        image_url: imageUrl,
        content_type: contentType,
        user_context: userContext,
        mime_type: mimeType
      });
      
      return response.data?.captions || [];
    } catch (error) {
      console.error('Erro ao gerar legendas:', error);
      throw error;
    }
  },
  
  /**
   * Gera legendas a partir de um arquivo de imagem
   * @param {File} file - Arquivo de imagem
   * @param {string} [contentType='marketing'] - Tipo de conteúdo
   * @param {string} [userContext=''] - Contexto adicional
   * @returns {Promise<CaptionItem[]>} Lista de legendas geradas
   */
  async generatePostCaptionsFromFile(file, contentType = 'marketing', userContext = '') {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = async () => {
        try {
          // Extrair base64 do resultado (remover prefixo data:image/xxx;base64,)
          const base64 = reader.result.split(',')[1];
          
          const captions = await this.generatePostCaptions({
            imageBase64: base64,
            contentType,
            userContext,
            mimeType: file.type
          });
          
          resolve(captions);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => reject(new Error('Erro ao ler arquivo'));
      reader.readAsDataURL(file);
    });
  },
  
  // ===========================================================================
  // GERAÇÃO DE PROMPTS PARA CONTEÚDO
  // ===========================================================================
  
  /**
   * Gera prompts otimizados baseados na ideia do usuário
   * @param {string} userIdea - Ideia ou pedido do usuário
   * @param {string} [contentType='marketing'] - Tipo de conteúdo
   * @returns {Promise<PromptItem[]>} Lista de prompts gerados
   */
  async generateContentPrompts(userIdea, contentType = 'marketing') {
    try {
      const response = await api.post('/api/ai/generate-content-prompts', {
        user_idea: userIdea,
        content_type: contentType
      });
      
      return response.data?.prompts || [];
    } catch (error) {
      console.error('Erro ao gerar prompts:', error);
      throw error;
    }
  },
  
  /**
   * Gera prompts baseados em ações rápidas pré-definidas
   * @param {string} quickAction - ID da ação rápida
   * @param {string} [productOrService=''] - Nome do produto/serviço
   * @param {string} [contentType='marketing'] - Tipo de conteúdo
   * @returns {Promise<PromptItem[]>} Lista de prompts gerados
   */
  async generateQuickPrompts(quickAction, productOrService = '', contentType = 'marketing') {
    try {
      const response = await api.post('/api/ai/generate-quick-prompts', {
        quick_action: quickAction,
        product_or_service: productOrService,
        content_type: contentType
      });
      
      return response.data?.prompts || [];
    } catch (error) {
      console.error('Erro ao gerar prompts rápidos:', error);
      throw error;
    }
  },
  
  // ===========================================================================
  // GERAÇÃO DE PROMPT A PARTIR DE LINK
  // ===========================================================================

  /**
   * Gera um prompt estratégico a partir de uma URL (notícia, trend, artigo)
   * @param {string} url - URL da página
   * @param {string} [contentType='marketing'] - Tipo de conteúdo
   * @returns {Promise<string>} Prompt gerado
   */
  async generatePromptFromLink(url, contentType = 'marketing') {
    try {
      const response = await api.post('/api/ai/generate-prompt-from-link', {
        url,
        content_type: contentType
      });
      return response.data?.prompt || '';
    } catch (error) {
      console.error('Erro ao gerar prompt de link:', error);
      throw error;
    }
  },

  // ===========================================================================
  // AÇÕES RÁPIDAS
  // ===========================================================================
  
  /**
   * Obtém a lista de ações rápidas disponíveis
   * @returns {Promise<QuickAction[]>} Lista de ações rápidas
   */
  async getQuickActions() {
    try {
      const response = await api.get('/api/ai/quick-actions');
      return response.data?.actions || [];
    } catch (error) {
      console.error('Erro ao obter ações rápidas:', error);
      // Retornar lista padrão em caso de erro
      return this.getDefaultQuickActions();
    }
  },
  
  /**
   * Retorna lista padrão de ações rápidas (fallback)
   * @returns {QuickAction[]}
   */
  getDefaultQuickActions() {
    return [
      {
        id: 'vender_produto',
        label: 'Vender Produto',
        emoji: '🛒',
        description: 'Criar post de venda com urgência',
        requires_product: true
      },
      {
        id: 'anunciar_promocao',
        label: 'Anunciar Promoção',
        emoji: '🏷️',
        description: 'Promoção especial com desconto',
        requires_product: false
      },
      {
        id: 'lancar_produto',
        label: 'Lançar Produto',
        emoji: '🚀',
        description: 'Anunciar lançamento de produto novo',
        requires_product: true
      },
      {
        id: 'engajar_audiencia',
        label: 'Engajar Audiência',
        emoji: '💬',
        description: 'Post interativo para engajamento',
        requires_product: false
      },
      {
        id: 'mostrar_bastidores',
        label: 'Mostrar Bastidores',
        emoji: '🎬',
        description: 'Behind the scenes do negócio',
        requires_product: false
      },
      {
        id: 'depoimento_cliente',
        label: 'Depoimento',
        emoji: '⭐',
        description: 'Destacar caso de sucesso',
        requires_product: false
      },
      {
        id: 'dica_educativa',
        label: 'Dica Educativa',
        emoji: '💡',
        description: 'Conteúdo educativo e dicas',
        requires_product: false
      },
      {
        id: 'data_comemorativa',
        label: 'Data Comemorativa',
        emoji: '🎉',
        description: 'Post para data especial',
        requires_product: false
      }
    ];
  }
};

export default aiGenerationService;